import React from 'react'
import {Col, Row} from 'react-bootstrap'
import {useLocation} from 'react-router'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {library} from '@fortawesome/fontawesome-svg-core'
import {fab, faTwitterSquare, faFacebook, faYoutube, faInstagram} from '@fortawesome/free-brands-svg-icons'
import {BaseButton} from 'iebt-zing-storybook'
import Tippy from '@tippyjs/react'
import { trans } from 'sharedKernel/i18n'
import { VisualizationScreenProps } from './contract'
import { 
    ScreenContainer, 
    HeaderWrapper, 
    HeaderButtons, 
    HeaderTitleWrapper,
 } from './styles'
import { EvaluatorsButton } from './Buttons/EvaluatorsButton'
import { LaunchChallengeButton } from './Buttons/LaunchChallengeButton'
import { LaunchIdeaButton } from './Buttons/LaunchIdeaButton'
import { Loading } from 'components/loading'
import { NoContent } from 'components/NoContent'
import { Timeline } from 'components/Timeline'
import { SolutionsButton } from './Buttons/SolutionsButton'
import DOMPurify from "dompurify"
import {
    isCurrentDateBiggerThanDeadline,
    isStartDateGreaterThanCurrentDate
} from 'sharedKernel/handlers'
import {
    AttachmentsArea,
    AttachmentsItem,
    AttachmentsTitle,
    CoverImage,
    DescriptionText,
    DescriptionTitle,
    IdeasLimit,
    LinksArea,
    PresentationArea,
    PresentationVideo,
    Title
} from 'sharedKernel/styledComponents/visualizationComponents/styles'
import {isUserOwner} from './behavior'
import {getProvider} from 'sharedKernel/api'
import {showToast} from 'components/toast'
import {triggerFileDownloadOnClientSide} from 'sharedKernel/file/handler'
import {ReactI18NextChild} from "react-i18next"
import {EditChallengeButton} from "./Buttons/EditChallengeButton";
import {RemoveChallengeButton} from "./Buttons/RemoveChallengeButton";


library.add(fab, faTwitterSquare, faFacebook, faYoutube, faInstagram)

export const VisualizationScreen = ({
    isFetching,
    preview = false,
    data,
    isFileFetching,
    fileData,
    readOnly = false
}: VisualizationScreenProps): JSX.Element => {
    const filesChallenge = {
        challengeFiles: fileData?.filter((file: { type: string }) => file.type === 'supportFiles'),
        homePageSlider: fileData?.find((file: { type: string }) => file.type === 'homePageSlider'),
        presentationVideo: fileData?.find((file: { type: string }) => file.type === 'presentationVideo'),
        coverImage: fileData?.find((file: { type: string }) => file.type === 'coverImage')
    }
    const {state}: any = useLocation()
    const {mutate: downloadFile} = getProvider()

    const labels = {
        notFound: trans('Não encontrado'),
        description: trans('Descrição'),
        attachments: trans('Anexos'),
        supportFilesNotFound: trans('Nenhum arquivo de apoio foi incluído neste evento'),
        closed: trans('Encerrado'),
        minute: trans('Exportar Ata'),
        minuteFallbackWarning: trans('A etapa atual não possui avaliações. A ata foi exportada com a etapa anterior.'),
        solutionLimit: trans('Limite de soluções atingido'),
        signUpSoon: trans('Inscreva-se em breve'),
        tooltipYoutubeIcon: trans('Vídeo de apresentação'),
        tooltipYoutube2: trans('Segundo Vídeo'),
        tooltipYoutube3: trans('Terceiro Vídeo')
    }

    if (isFetching ?? false) {
        return (
            <Loading/>
        )
    }
    if (data === undefined) {
        return <NoContent text={labels.notFound}/>
    }

    const isSubscriptionClosed = isCurrentDateBiggerThanDeadline(data.data.applicationForm?.finalSelectionDate)
    const startDateNotReached = isStartDateGreaterThanCurrentDate(data.data.applicationForm?.applicationStartDate)

    const isSolutionsLimitAchieved = (): boolean => {
        const hasNumberOfSolutions = data.data.config.numberOfSolutions !== null

        if (!hasNumberOfSolutions) {
            return false
        }

        if (preview) {
            return false
        }

        return data.data.config.numberOfSolutions >= data.data.config.limitOfIdeas
    }

    const ownerId = data.data.owner?.id
    const isOwner = ownerId ? isUserOwner(ownerId) : false
    const challengeId = data.id
    let isLaunched = data.data.isLaunched
    const youtubeLink = data.data.style.youtubeLink
    const youtubeLink2 = data.data.style.youtubeLink2
    const youtubeLink3 = data.data.style.youtubeLink3

    const youtubeLinkTitle = data.data.style.youtubeLinkTitle !== '' && data.data.style.youtubeLinkTitle !== undefined && data.data.style.youtubeLinkTitle !== null ? data.data.style.youtubeLinkTitle : data.data.style.youtubeLink
    const youtubeLink2Title = data.data.style.youtubeLink2Title !== '' && data.data.style.youtubeLink2Title !== undefined && data.data.style.youtubeLink2Title !== null ? data.data.style.youtubeLink2Title : data.data.style.youtubeLink2
    const youtubeLink3Title = data.data.style.youtubeLink3Title !== '' && data.data.style.youtubeLink3Title !== undefined && data.data.style.youtubeLink3Title !== null ? data.data.style.youtubeLink3Title : data.data.style.youtubeLink

    const remainingAttempts = data.data.config.limitOfIdeas - data.data.config.numberOfIdeas
    const solutionsLimitAchieved = isSolutionsLimitAchieved()
    if (state !== null) {
        isLaunched = state.setToLaunched
    }

    const getIdeaImage = (url?: string): string => {
        const fallBackIdeaImage = require('../../../../assets/images/Imagem_do_programa.png')

        return url != null && url.length > 0 ? url : fallBackIdeaImage
    }

    function tryCreateAtaButton() {
        if (!isOwner) return;
        return (
            <div className="pb-4">
                <BaseButton
                    btnContent={<span style={{
                        fontSize: '18px',
                        fontWeight: 700,
                        color: 'var(--primary)',
                        lineHeight: '16px'
                    }}>{labels.minute}</span>}
                    onClick={() => {
                        downloadFile(
                            {
                                url: `/v1/hub/company/challenge/${challengeId}/report/analytical/minute`,
                                httpConfig: {responseType: 'blob'}
                            },
                            {
                                onSuccess: (res) => {
                                    triggerFileDownloadOnClientSide(res.data, `Ata - ${data.data.config.title}.docx`)
                                    const usedFallback =
                                        res.headers?.['x-minute-report-fallback'] === 'true' ||
                                        res.headers?.['X-Minute-Report-Fallback'] === 'true'
                                    if (usedFallback) {
                                        showToast('warning', labels.minuteFallbackWarning)
                                    }
                                },
                                onError: () => {
                                    showToast('error', 'Ocorreu um erro ao baixar o arquivo. Por favor, tente novamente mais tarde.')
                                }
                            }
                        )
                    }}
                    variant="secondary"/>
            </div>

        )
    }

    const tryCreteSubscribeButton = (): JSX.Element => {
        if (isSubscriptionClosed) {
            return (
                <IdeasLimit>{labels.closed}</IdeasLimit>
            )
        }

        if (solutionsLimitAchieved) {
            return (
                <IdeasLimit>{labels.solutionLimit}</IdeasLimit>
            )
        }

        if (startDateNotReached) {
            return (
                <IdeasLimit>{labels.signUpSoon}</IdeasLimit>
            )
        }

        return (
            <LaunchIdeaButton challengeId={challengeId} preview={preview} remainingAttempts={remainingAttempts} />
        )
    }

    function getHeader() {
        return <>
            <HeaderWrapper>
                <HeaderTitleWrapper>
                    <Title>{data.data.config.title}</Title>
                </HeaderTitleWrapper>
                <HeaderButtons>
                    <EditChallengeButton challengeId={data.id} isOwner={isOwner} alreadyLaunched={isLaunched} />
                    <RemoveChallengeButton challengeId={data.id} isOwner={isOwner} alreadyLaunched={isLaunched} />
                </HeaderButtons>
            </HeaderWrapper>
        </>;
    }

    function getHomePageSlider() {
        return <CoverImage backgroundImg={getIdeaImage(filesChallenge?.homePageSlider?.path)} />
    }

    function getDtailPanel() {
        return (
            <>
                <DescriptionTitle>{labels.description}</DescriptionTitle>
                <hr />
                <DescriptionText>
                    {data.data.style.shortDescription}
                    <br />
                    <div style={{ marginTop: '2rem' }}>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(data.data.style.longDescription)
                            }}
                        />
                    </div>
                </DescriptionText>

                <PresentationArea className="d-flex justify-content-center">
                    {!isFileFetching && filesChallenge.presentationVideo !== undefined ? (
                        <PresentationVideo src={filesChallenge.presentationVideo.path} controls />
                    ) : (
                        <p></p>
                    )}
                </PresentationArea>
            </>
        );
    }

    function getGeneralPanel() {
        return <Row>
            {!readOnly && (!isLaunched ? (
                <Col sm={12} className="gy-4">
                    <LaunchChallengeButton challengeId={challengeId} isOwner={isOwner}
                        alreadyLaunched={isLaunched} />
                </Col>
            ) : (
                <Col sm={12} className="gy-3">
                    <EvaluatorsButton challengeId={challengeId} isOwner={isOwner}
                        alreadyLaunched={isLaunched} />
                    <SolutionsButton challengeId={challengeId} isOwner={isOwner}
                        alreadyLaunched={isLaunched} />
                    {tryCreateAtaButton()}
                    {tryCreteSubscribeButton()}
                </Col>
            ))}
            {(youtubeLink || youtubeLink2 || youtubeLink3) && (
                <Col sm={12} className="gy-3">
                    {youtubeLink !== '' ? (
                        <Tippy
                            content={data.data.style.youtubeLinkTitle !== '' && data.data.style.youtubeLinkTitle !== undefined && data.data.style.youtubeLinkTitle !== null ? trans(data.data.style.youtubeLinkTitle) : labels.tooltipYoutubeIcon}
                            animation='scale'
                            arrow={false}
                            maxWidth="100%"
                            theme='primary'
                            allowHTML={true}
                        >
                            <a href={youtubeLink} target="_blank" className="me-2">
                                <BaseButton
                                    btnContent={<FontAwesomeIcon icon={['fab', 'youtube']} size="xl" />}
                                    variant="primary-invert"
                                    size="icon"
                                />
                            </a>
                        </Tippy>
                    ) : (
                        <></>
                    )}
                    {(youtubeLink2 !== '' && youtubeLink2 !== undefined && youtubeLink2 !== null) ? (
                        <Tippy
                            content={data.data.style.youtubeLink2Title !== '' && data.data.style.youtubeLink2Title !== undefined && data.data.style.youtubeLink2Title !== null ? trans(data.data.style.youtubeLink2Title) : labels.tooltipYoutube2}
                            animation='scale'
                            arrow={false}
                            maxWidth="100%"
                            theme='primary'
                            allowHTML={true}
                        >
                            <a href={youtubeLink2} target="_blank" className="me-2">
                                <BaseButton
                                    btnContent={<FontAwesomeIcon icon={['fab', 'youtube']} size="xl" />}
                                    variant="primary-invert"
                                    size="icon"
                                />
                            </a>
                        </Tippy>
                    ) : (
                        <></>
                    )}
                    {(youtubeLink3 !== '' && youtubeLink3 !== undefined && youtubeLink3 !== null) ? (
                        <Tippy
                            content={data.data.style.youtubeLink3Title !== '' && data.data.style.youtubeLink3Title !== undefined && data.data.style.youtubeLink3Title !== null ? trans(data.data.style.youtubeLink3Title) : labels.tooltipYoutube3}
                            animation='scale'
                            arrow={false}
                            maxWidth="100%"
                            theme='primary'
                            allowHTML={true}
                        >
                            <a href={youtubeLink3} target="_blank">
                                <BaseButton
                                    btnContent={<FontAwesomeIcon icon={['fab', 'youtube']} size="xl" />}
                                    variant="primary-invert"
                                    size="icon"
                                />
                            </a>
                        </Tippy>
                    ) : (
                        <></>
                    )}
                </Col>
            )}
            <Col sm={12} className="gy-4">
                <AttachmentsArea>
                    <AttachmentsTitle>{labels.attachments}:</AttachmentsTitle>
                    {!isFileFetching && filesChallenge.challengeFiles !== undefined && filesChallenge.challengeFiles.length > 0 ? (
                        filesChallenge.challengeFiles.map((file: {
                            path: string | undefined;
                            name: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal | Iterable<ReactI18NextChild> | null | undefined
                        }, index: any) => {
                            return (
                                <AttachmentsItem key={`attachments-item-${index}`}>
                                    <a key={`item-${index}`} href={file.path} target="_blank"
                                        download={file.name}>
                                        {file.name}
                                    </a>
                                </AttachmentsItem>
                            )
                        })
                    ) : (
                        <span>{trans('Não informado')}</span>
                    )}
                </AttachmentsArea>
            </Col>
            {youtubeLink && (
                <LinksArea>
                    <a href={youtubeLink} target="_blank">{youtubeLinkTitle}</a>
                </LinksArea>
            )}
            {(youtubeLink2 && youtubeLink2 !== '') && (
                <LinksArea>
                    <a href={youtubeLink2} target="_blank">{youtubeLink2Title}</a>
                </LinksArea>
            )}
            {(youtubeLink3 && youtubeLink3 !== '') && (
                <LinksArea>
                    <a href={youtubeLink3} target="_blank">{youtubeLink3Title}</a>
                </LinksArea>
            )}
        </Row>;
    }

    return (
        <ScreenContainer>
            <Row className="mb-3">
                <Col>
                    {getHeader()}
                </Col>
            </Row>
            <Row className="mb-4  ml-4  mr-4">
                <Col>
                    {getHomePageSlider()}
                </Col>
            </Row>
            <Row className="mb-4">
                <Col className="d-flex justify-content-center align-items-center">
                    <Timeline steps={data.data} />
                </Col>
            </Row>
            <Row>
                <Col sm={4} xxl={2}>
                    {getGeneralPanel()}
                </Col>
                <Col sm={7} xxl={9}>
                    {getDtailPanel()}
                </Col>
            </Row>
        </ScreenContainer>
    )

}
